/**
 * Shared constants for the tools authoring surfaces.
 *
 * Extracted from the 5 tool route files during PR-C-MINIMAL
 * (2026-05-12) — see the prior PR-C stop report. Tenant + sysadmin
 * tool authoring surfaces share only ~20-30% of their code, so the
 * full <ToolsAuthoring scope> shared-component refactor was rejected.
 * What's safe to dedupe is small, additive, pure constants + helpers:
 * this file holds the constants, the sibling `tools-shared.helpers.ts`
 * holds the pure functions.
 *
 * Consumers:
 *   - packages/web/src/routes/studio/tools.new.tsx
 *   - packages/web/src/routes/studio/tools.$toolId.tsx
 *   - packages/web/src/routes/sysadmin/studio/tools.tsx
 *   - packages/web/src/routes/sysadmin/catalog.tools.tsx
 *   - packages/web/src/routes/sysadmin/catalog.tools.$id.tsx
 *
 * No React state, no JSX — pure data + a single icon-aware helper.
 */

import {
  Plug, Globe, Code, Database, Sparkles,
  type LucideIcon,
} from 'lucide-react';

// ─── Policy ──────────────────────────────────────────────────────────────────

/**
 * Tool execution policy enum. The on-disk schema is shared between
 * `workspace_tools` (tenant) and `system_tools` (sysadmin), so the
 * tuple is the source of truth for both consumers. Each consumer
 * keeps its own typed `ToolPolicy` re-export from its hook file
 * (`use-workspace-tools` vs `use-sysadmin-tools`) — those types are
 * derived from the same enum and are interchangeable at runtime.
 */
export const POLICIES = ['auto_approve', 'require_approval', 'forbidden'] as const;

export type ToolPolicy = (typeof POLICIES)[number];

/**
 * Long-form human description per policy. Used by the tenant create
 * page (`studio/tools.new.tsx`) and the tenant detail page
 * (`studio/tools.$toolId.tsx` — historically used a slightly shorter
 * variant; both pages now share this fuller copy so users see the
 * same explanation on every surface).
 */
export const POLICY_DESCRIPTIONS: Record<ToolPolicy, string> = {
  auto_approve:
    'Runs without confirmation when an agent calls it. Use for read-only or low-risk tools.',
  require_approval:
    'Pauses the agent run; user confirms before execution. Use for tools with side effects.',
  forbidden:
    'Cannot be invoked. Useful for soft-disabling sensitive tools without deleting them.',
};

// ─── Source kinds ────────────────────────────────────────────────────────────

/**
 * Catalog of tool source types. `custom` is the tenant-side label
 * for the same row that sysadmin sees as `http` (the workspace_tools
 * tableserialises HTTP-handler rows with `tool_source='custom'`). The
 * sysadmin catalog table is more granular and additionally has
 * `custom_function`; both labels live in `SOURCE_META` below.
 */
export const SOURCE_TYPES = [
  'builtin',
  'mcp',
  'openapi',
  'http',
  'custom',
  'database',
  'custom_function',
  // AUTO-7: composition tools (wrapper + chain patterns) that compose other catalog tools.
  'composition',
] as const;

export type SourceType = (typeof SOURCE_TYPES)[number];

/**
 * Per-source-type icon + label + className triple. Originated in
 * `studio/tools.$toolId.tsx`. The detail-page palette is the canonical
 * one (richer descriptions, dark-mode-aware colours). Sysadmin list
 * pages currently render their own badge components (StatusBadge +
 * SourceKindBadge in `catalog.tools.tsx`) — those are NOT migrated
 * here because they share no class strings with this palette
 * (different colour family; sysadmin uses violet/blue/teal vs tenant
 * amber/emerald/purple). Migrating them would be a behavioural
 * change, not a deduplication; out of scope for PR-C-MINIMAL.
 */
export interface SourceMeta {
  label: string;
  cls: string;
  icon: LucideIcon;
  description: string;
}

export const SOURCE_META: Record<string, SourceMeta> = {
  builtin: {
    label: 'Built-in',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    icon: Sparkles,
    description: 'Ships with the platform.',
  },
  mcp: {
    label: 'MCP',
    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
    icon: Plug,
    description: 'External MCP server connection.',
  },
  openapi: {
    label: 'OpenAPI',
    cls: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
    icon: Globe,
    description: 'Generated from an OpenAPI / Swagger spec.',
  },
  custom: {
    label: 'HTTP',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: Code,
    description: 'Custom HTTP endpoint.',
  },
  database: {
    label: 'Database',
    cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30',
    icon: Database,
    description: 'Parameterized SQL query.',
  },
  // AUTO-7
  composition: {
    label: 'Composition',
    cls: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
    icon: Plug,
    description: 'Wraps or chains other catalog tools (AUTO-7).',
  },
};

/**
 * Safe lookup for an unknown source string. Falls back to a neutral
 * Plug icon + muted styling rather than throwing — the catalog can
 * grow source kinds (e.g. `custom_function`) without crashing older
 * detail pages.
 */
export function getSourceMeta(s: string): SourceMeta {
  return (
    SOURCE_META[s] ?? {
      label: s,
      cls: 'bg-muted text-muted-foreground border-border',
      icon: Plug,
      description: 'Custom tool source.',
    }
  );
}
